import { doc, getDoc } from "firebase/firestore";
import { studioDb } from "../firebase";

const inFlightAvatarRequests = new Map();
const inFlightMarkerRequests = new Map();
const debugSponsoredAvatar =
  String(process.env.REACT_APP_DEBUG_SPONSORED_AVATAR || "").toLowerCase() === "true";

function normalizeAvatarUrl(value) {
  return String(value || "").trim();
}

function toFiniteNumber(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function emptyMarkerMetadata() {
  return { avatarUrl: "", companyName: "", locations: [] };
}

function buildLocation({
  id = "",
  name = "",
  address = "",
  placeId = "",
  lat = null,
  lng = null,
  isHeadquarters = false,
}) {
  const normalizedName = String(name || "").trim();
  const normalizedAddress = String(address || "").trim();
  const normalizedPlaceId = String(placeId || "").trim();
  if (!normalizedName && !normalizedAddress && !normalizedPlaceId) return null;
  return {
    id: String(id || "").trim(),
    name: normalizedName,
    address: normalizedAddress,
    placeId: normalizedPlaceId,
    lat: toFiniteNumber(lat),
    lng: toFiniteNumber(lng),
    isHeadquarters: isHeadquarters === true,
  };
}

// Studio's source of truth. `label` is the owner-chosen name; `address` is the
// real street address. Only this path can tell the two apart.
function locationsFromEntries(entries = []) {
  return entries
    .map((entry) =>
      buildLocation({
        id: entry?.id,
        name: entry?.label,
        address: entry?.address,
        placeId: entry?.placeId,
        lat: entry?.lat,
        lng: entry?.lng,
        isHeadquarters: entry?.isHeadquarters,
      })
    )
    .filter(Boolean);
}

// Businesses saved before locationEntries existed. Studio's legacy mirror writes
// `address: entry.label || entry.address`, so a name here is indistinguishable
// from an address — leave `name` empty rather than guess.
function locationsFromLegacyFields(payload = {}) {
  const locations = [];
  const seen = new Set();
  const addLocation = (location) => {
    if (!location) return;
    const key = [location.placeId, location.address.toLowerCase(), location.lat, location.lng].join(
      "|"
    );
    if (seen.has(key)) return;
    seen.add(key);
    locations.push(location);
  };

  addLocation(
    buildLocation({
      address: payload?.hqAddress,
      placeId: payload?.hqPlaceId,
      lat: payload?.hqLocation?.lat,
      lng: payload?.hqLocation?.lng,
      isHeadquarters: true,
    })
  );

  const fromEntry = (entry) => {
    if (typeof entry === "string") return buildLocation({ address: entry });
    return buildLocation({
      address: entry?.address || entry?.formattedAddress || entry?.formatted_address || entry?.label,
      placeId: entry?.placeId || entry?.place_id,
      lat: entry?.lat ?? entry?.latitude ?? entry?.location?.lat ?? entry?.geo?.lat,
      lng:
        entry?.lng ??
        entry?.lon ??
        entry?.longitude ??
        entry?.location?.lng ??
        entry?.location?.lon ??
        entry?.location?.longitude ??
        entry?.geo?.lng,
    });
  };

  (Array.isArray(payload?.locationDetails) ? payload.locationDetails : []).forEach((entry) =>
    addLocation(fromEntry(entry))
  );
  (Array.isArray(payload?.locations) ? payload.locations : []).forEach((entry) =>
    addLocation(fromEntry(entry))
  );

  return locations;
}

function normalizeBusinessMarkerPayload(payload = {}) {
  const entries = Array.isArray(payload?.locationEntries) ? payload.locationEntries : [];
  return {
    avatarUrl: normalizeAvatarUrl(payload?.pinUrl || payload?.avatarUrl || ""),
    companyName: String(payload?.companyName || payload?.displayName || payload?.name || "").trim(),
    locations: entries.length ? locationsFromEntries(entries) : locationsFromLegacyFields(payload),
  };
}

// Panning the map re-requests the same businesses constantly. Cache the resolved
// result, but expire it so a pin or location edit still surfaces within a session.
const BUSINESS_MARKER_CACHE_TTL_MS = 5 * 60 * 1000;
const businessMarkerCache = new Map();

export function clearBusinessMarkerCache() {
  businessMarkerCache.clear();
}

export async function fetchBusinessMarkerMetadata(businessId) {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId || !studioDb) return emptyMarkerMetadata();

  const cached = businessMarkerCache.get(normalizedBusinessId);
  if (cached && Date.now() - cached.at <= BUSINESS_MARKER_CACHE_TTL_MS) {
    return cached.value;
  }

  if (inFlightMarkerRequests.has(normalizedBusinessId)) {
    return inFlightMarkerRequests.get(normalizedBusinessId);
  }

  const request = (async () => {
    try {
      const businessRef = doc(studioDb, "businesses", normalizedBusinessId);
      const snapshot = await getDoc(businessRef);
      const payload = snapshot.exists() ? (snapshot.data() || {}) : {};
      const metadata = snapshot.exists()
        ? normalizeBusinessMarkerPayload(payload)
        : emptyMarkerMetadata();
      businessMarkerCache.set(normalizedBusinessId, { at: Date.now(), value: metadata });
      if (debugSponsoredAvatar) {
        console.log("[sponsored-avatar] business marker fetch", {
          businessId: normalizedBusinessId,
          docExists: snapshot.exists(),
          avatarUrl: metadata.avatarUrl,
          companyName: metadata.companyName,
          locationCount: metadata.locations.length,
        });
      }
      return metadata;
    } catch (err) {
      console.warn("Failed to fetch business marker metadata:", normalizedBusinessId, err);
      return emptyMarkerMetadata();
    } finally {
      inFlightMarkerRequests.delete(normalizedBusinessId);
    }
  })();

  inFlightMarkerRequests.set(normalizedBusinessId, request);
  return request;
}

export async function fetchBusinessAvatarUrl(businessId) {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId || !studioDb) return "";

  if (inFlightAvatarRequests.has(normalizedBusinessId)) {
    return inFlightAvatarRequests.get(normalizedBusinessId);
  }

  const request = (async () => {
    try {
      const businessRef = doc(studioDb, "businesses", normalizedBusinessId);
      const snapshot = await getDoc(businessRef);
      const payload = snapshot.exists() ? (snapshot.data() || {}) : {};
      const avatarUrl = normalizeAvatarUrl(
        payload?.pinUrl ||
        payload?.avatarUrl ||
        ""
      );
      if (debugSponsoredAvatar) {
        console.log("[sponsored-avatar] business fetch", {
          businessId: normalizedBusinessId,
          studioDbAvailable: Boolean(studioDb),
          projectId: studioDb?.app?.options?.projectId || "",
          databaseId:
            studioDb?._databaseId?.database ||
            studioDb?._databaseId?.databaseId ||
            "",
          docExists: snapshot.exists(),
          avatarUrl,
          payloadKeys: Object.keys(payload || {}),
        });
      }
      return avatarUrl;
    } catch (err) {
      console.warn("Failed to fetch business avatar:", normalizedBusinessId, err);
      return "";
    } finally {
      inFlightAvatarRequests.delete(normalizedBusinessId);
    }
  })();

  inFlightAvatarRequests.set(normalizedBusinessId, request);
  return request;
}

export async function getSponsoredBusinessAvatarMap(businessIds = []) {
  const uniqueIds = Array.from(
    new Set(
      (Array.isArray(businessIds) ? businessIds : [])
        .map((businessId) => String(businessId || "").trim())
        .filter(Boolean)
    )
  );

  if (uniqueIds.length === 0) return {};

  const entries = await Promise.all(
    uniqueIds.map(async (businessId) => [businessId, await fetchBusinessAvatarUrl(businessId)])
  );

  return entries.reduce((map, [businessId, avatarUrl]) => {
    map[businessId] = normalizeAvatarUrl(avatarUrl);
    return map;
  }, {});
}

export async function getSponsoredBusinessMarkerMap(businessIds = []) {
  const uniqueIds = Array.from(
    new Set(
      (Array.isArray(businessIds) ? businessIds : [])
        .map((businessId) => String(businessId || "").trim())
        .filter(Boolean)
    )
  );

  if (uniqueIds.length === 0) return {};

  const entries = await Promise.all(
    uniqueIds.map(async (businessId) => [businessId, await fetchBusinessMarkerMetadata(businessId)])
  );

  return entries.reduce((map, [businessId, metadata]) => {
    map[businessId] = {
      avatarUrl: normalizeAvatarUrl(metadata?.avatarUrl || ""),
      companyName: String(metadata?.companyName || "").trim(),
      locations: Array.isArray(metadata?.locations) ? metadata.locations : [],
    };
    return map;
  }, {});
}

export function resolveSponsoredAvatar({
  capsuleType = "",
  businessAvatarUrl = "",
  creatorProfileImage = "",
  senderProfileImage = "",
} = {}) {
  const type = String(capsuleType || "").toLowerCase();
  if (type !== "sponsored") {
    return normalizeAvatarUrl(creatorProfileImage || senderProfileImage);
  }

  const resolvedAvatar = normalizeAvatarUrl(
    businessAvatarUrl || creatorProfileImage || senderProfileImage
  );
  if (debugSponsoredAvatar) {
    console.log("[sponsored-avatar] resolve", {
      capsuleType: type,
      hasBusinessAvatarUrl: Boolean(normalizeAvatarUrl(businessAvatarUrl)),
      hasCreatorProfileImage: Boolean(normalizeAvatarUrl(creatorProfileImage)),
      hasSenderProfileImage: Boolean(normalizeAvatarUrl(senderProfileImage)),
      resolvedAvatar,
    });
  }
  return resolvedAvatar;
}
